"use strict";

const PhotoSwipeLightbox = require("photoswipe/lightbox").default;

/**
* An immutable object containing constants and configuration settings for the PhotoSwipe image zoom and thumbnail carousel features.
* @property {string} ZOOM_IMAGES_WRAPPER_SELECTOR - CSS selector for the zoom images wrapper element.
* @property {string} ZOOM_IMAGE_LINK_SELECTOR - CSS selector for the zoom image link elements.
* @property {string} THUMB_CONTAINER_CLASS_NAME - Class name for the thumbnails container in PhotoSwipe.
* @property {Object} THUMBNAILS_CAROUSEL_SETTINGS - Configuration object for the thumbnail carousel, including carousel behavior and appearance.
* @property {number} THUMBNAILS_MIN_INIT_CAROUSEL_NUM - Minimum number of thumbnails required to initialize the carousel.
* @property {string} WRAPPER_SELECTOR - Dynamically computed CSS selector for the image wrapper, depending on the device breakpoint.
*/
const constants = Object.freeze({
    ZOOM_IMAGES_WRAPPER_SELECTOR: ".zoom-images-wrapper",
    ZOOM_IMAGE_LINK_SELECTOR: ".zoom-image-link",
    THUMB_CONTAINER_CLASS_NAME: "pswp__thumbnails-container",
    THUMBNAILS_CAROUSEL_SETTINGS: {
        infinite: false,
        slidesToScroll: 1,
        swipeToSlide: true,
        dots: false,
        vertical: true,
        verticalSwiping: true,
        focusOnSelect: true,
        slidesToShow: 6,
        draggable: true
    },
    THUMBNAILS_MIN_INIT_CAROUSEL_NUM: 6,
    get WRAPPER_SELECTOR() {
        return isPhoneBreakpoint() ? `${this.ZOOM_IMAGES_WRAPPER_SELECTOR}.mobile-zoom-images` : `${this.ZOOM_IMAGES_WRAPPER_SELECTOR}:not(.mobile-zoom-images)`;
    }
});

/**
* Updates the enabled/disabled state of zoom in and zoom out buttons based on the current zoom level of a slide.
*
* @param {Object} slide - The slide object containing zoom information.
* @returns {void}
*/
function handleZoomIconState(slide) {
    if (!slide) return;

    const { secondary, initial } = slide.zoomLevels;
    const { currZoomLevel, currentResolution } =  slide;
    const zoomInBtn = document.querySelector(".pswp__button--zoom");
    const zoomOutBtn = document.querySelector(".pswp__button--zoom-out");

    if (!zoomInBtn || !zoomOutBtn) return;

    // currentResolution is always 0, when image changed or on initial open of the zoom
    if (!currentResolution) {
        // set buttons default state
        zoomInBtn.disabled = false;
        zoomOutBtn.disabled = true;
        return;
    }

    // secondary zoom level is the maximum when image is zoomed in
    zoomInBtn.disabled = currZoomLevel === secondary;
    // min zoom level is the initial, after the first zoom in
    zoomOutBtn.disabled = currZoomLevel === initial;
}

/**
* Attaches event listeners to handle image zoom and thumbnail carousel functionality on the product detail page.
*
* - Binds a click event to product images to trigger the corresponding zoom link based on the clicked image's index.
* - Listens for the 'imageSizeChange' event from the lightbox to update the zoom icon state.
* - Initializes the thumbnails carousel on the lightbox's 'firstUpdate' event.
*
* @param {Object} lightbox - The PhotoSwipe lightbox instance used for displaying product images.
*/
function handleEventListeners(lightbox) {
    const $pdpContainer = $(".pdp-container");
    const { WRAPPER_SELECTOR } = constants;
    const zoomImagesWrapper = document.querySelector(WRAPPER_SELECTOR);

    if (!$pdpContainer.length || !zoomImagesWrapper) return;

    $pdpContainer.on("click", ".pdp-image", event => {
        const index = event.target.getAttribute("data-index");
        // get the zoom link based on the index of the clicked image
        const zoomLink = zoomImagesWrapper.querySelector(`:scope ${constants.ZOOM_IMAGE_LINK_SELECTOR}:nth-of-type(${index})`);

        if (!zoomLink) return;

        // trigger zoom link click
        zoomLink.click();
    });

    lightbox.on("imageSizeChange", ({ slide }) => handleZoomIconState(slide));
    // init thumbnails carousel
    lightbox.on("firstUpdate", () => handleThumbnailsCarousel());
}

/**
* Toggles the zoom level of the current slide between initial and secondary zoom states.
* @param {Object} currSlide - The current slide object, expected to have zoomTo, currZoomLevel, zoomLevels, and bounds properties.
*/
function zoomOut(currSlide) {
    // zoom slide to
    currSlide.zoomTo(
        currSlide.currZoomLevel === currSlide.zoomLevels.initial ?
            currSlide.zoomLevels.secondary :
            currSlide.zoomLevels.initial, // slide zoom level, 1 - original image size
        currSlide.bounds.min, // zoom center point
        333, // photoswipe initial zoom duration
    );
}
/**
* Determines if the current device viewport is in portrait orientation and has a maximum width of 1024px.
* Typically used to detect tablet/phone portrait mode for responsive design adjustments.
* @returns {boolean} True if the viewport matches phone/tablet portrait criteria, otherwise false.
*/
function isPhoneBreakpoint() {
    const TABLET_PORTRAIT_BREAKPOINT = "1024px";
    return window.matchMedia(`(max-width: ${TABLET_PORTRAIT_BREAKPOINT}) and (orientation:portrait)`).matches;
}

/**
* Returns the configuration object for the zoom out button in the PhotoSwipe gallery.
* @returns {Object} Configuration object for the zoom out icon, including properties such as name, className, tagName, appendTo, and event handlers for initialization and click events.
*/
function getZoomOutIconConfig() {
    return {
        name: "zoomOutIcon",
        className: "pswp__button pswp__button--zoom-out",
        tagName: "button",
        appendTo: "wrapper",
        onClick: (event, el, { currSlide }) => {
            zoomOut(currSlide);
        }
    };
}

/**
* Initializes the thumbnails carousel for the PhotoSwipe gallery using Slick Carousel.
* Applies the carousel settings defined in constants to the thumbnails container element.
* If the thumbnails container is not present in the DOM, the function exits without action.
*
* @returns {void}
*/
function handleThumbnailsCarousel() {
    const { THUMBNAILS_CAROUSEL_SETTINGS, THUMBNAILS_MIN_INIT_CAROUSEL_NUM, THUMB_CONTAINER_CLASS_NAME} = constants;
    const thumbnailsContainer = document.querySelector(`.${THUMB_CONTAINER_CLASS_NAME}`);

    if (!thumbnailsContainer || thumbnailsContainer.children.length <= THUMBNAILS_MIN_INIT_CAROUSEL_NUM) return;

    $(thumbnailsContainer).slick(THUMBNAILS_CAROUSEL_SETTINGS);
}

/**
* Generates the configuration object for initializing and rendering zoom thumbnails in the PhotoSwipe gallery.
*
* The configuration includes properties for the thumbnails container, initialization logic for creating thumbnail elements,
* and event handling to synchronize the active thumbnail with the current slide. Thumbnails are dynamically generated from
* images within a specified wrapper, and clicking a thumbnail navigates to the corresponding image in the gallery.
* Thumbnails are not rendered on phone portrait breakpoints.
*
* @returns {Object} Configuration object for PhotoSwipe thumbnails, including container details and initialization callback.
*/
function getThumbnailsConfig() {
    const { THUMB_CONTAINER_CLASS_NAME } = constants;
    return {
        name: "zoomThumbnails",
        className: THUMB_CONTAINER_CLASS_NAME,
        appendTo: "wrapper",
        onInit: (el, pswp) => {
            // no thumbnails needed on phone portrait
            if (isPhoneBreakpoint()) return;

            const thumbnails = [];
            let thumbnail;
            let prevIndex = -1;

            const currentZoomImagesList = document.querySelectorAll(".product-thumbnails-list .product-thumbnail-img");

            if (!currentZoomImagesList.length) return;

            currentZoomImagesList.forEach(imgEl => {
                thumbnail = document.createElement("img");
                thumbnail.className = "pswp__thumbnail";
                thumbnail.src = imgEl.src;
                thumbnail.setAttribute("itemprop", imgEl.getAttribute("itemprop") || "image");
                thumbnail.setAttribute("alt", imgEl.alt);
                thumbnail.setAttribute("width", imgEl.width || 100);
                thumbnail.setAttribute("height", imgEl.height || 118);

                thumbnail.onclick = event => {
                    pswp.goTo(thumbnails.indexOf(event.target));
                };

                el.appendChild(thumbnail);
                thumbnails.push(thumbnail);
            });

            pswp.on("change", () => {
                const $thumbsContainer = $(`.${THUMB_CONTAINER_CLASS_NAME}`);

                if (prevIndex >= 0) {
                    thumbnails[prevIndex].classList.remove("pswp__thumbnail--active");
                }

                if ($thumbsContainer.length && $thumbsContainer.hasClass("slick-initialized")) {
                    $thumbsContainer.slick("slickGoTo", pswp.currIndex);
                }

                thumbnails[pswp.currIndex].classList.add("pswp__thumbnail--active");
                prevIndex = pswp.currIndex;
            });
        }
    };
}

/**
* Returns the configuration object for a custom bullets indicator used in PhotoSwipe galleries.
* The indicator displays navigation bullets corresponding to each gallery item, highlights the active bullet,
* and allows users to navigate to a specific item by clicking a bullet.
*
* @returns {Object} Configuration object for the bullets indicator, including its name, class, parent element,
*                   and initialization logic for creating and managing bullet elements.
*/
function getBulletsIndicatorConfig() {
    return {
        name: "zoomBulletsIndicator",
        className: "pswp__bullets-indicator",
        appendTo: "wrapper",
        onInit: (el, pswp) => {
            const bullets = [];
            let bullet;
            let prevIndex = -1;

            for (let i = 0; i < pswp.getNumItems(); i++) {
                bullet = document.createElement("div");
                bullet.className = "pswp__bullet";

                bullet.onclick = (e) => {
                    pswp.goTo(bullets.indexOf(e.target));
                };

                el.appendChild(bullet);
                bullets.push(bullet);
            }

            pswp.on("change", () => {
                if (prevIndex >= 0) {
                    bullets[prevIndex].classList.remove("pswp__bullet--active");
                }

                bullets[pswp.currIndex].classList.add("pswp__bullet--active");
                prevIndex = pswp.currIndex;
            });
        }
    };
}

/**
* Registers custom UI elements (bullets indicator, zoomOutIcon,thumbnails) to the PhotoSwipe lightbox instance.
* @param {Object} lightbox - The PhotoSwipe lightbox instance to which UI elements will be added.
* @returns {void}
*/
function addUIElements(lightbox) {
    if (!lightbox) return;

    lightbox.on("uiRegister", () => {
        [
            getBulletsIndicatorConfig(),
            getThumbnailsConfig(),
            getZoomOutIconConfig()
        ].forEach(element => {
            lightbox.pswp.ui.registerElement(element);
        });
    });
}

/**
* Generates and returns the configuration options object for initializing PhotoSwipe.
*
* @returns {Object} PhotoSwipe options including gallery selector, zoom levels, animation type, navigation controls, and module loader.
*/
function getPhotoSwipeOptions() {
    return {
        gallery: constants.WRAPPER_SELECTOR,
        children: "a",
        showHideAnimationType: "fade",
        initialZoomLevel: "fill",
        secondaryZoomLevel: isPhoneBreakpoint() ? "fill" : 1.5,
        maxZoomLevel: 1,
        counter: false, // disable default counter
        allowPanToNext: isPhoneBreakpoint() ? true : false, // disable swipe to next when zoomed in
        imageClickAction: false,
        doubleTapAction: false,
        pswpModule: () => require("photoswipe")
    };
}

/**
* Prepares images in the gallery for PhotoSwipe by preloading their dimensions.
*
* This function selects all image anchor elements specified by the ZOOM_IMAGE_LINK_SELECTOR,
* loads each image asynchronously to retrieve its natural width and height, and stores these
* dimensions as data attributes on the corresponding elements. It ensures all images are processed
* before resolving, which is useful for initializing PhotoSwipe with accurate image sizes.
*
* @async
* @returns {Promise<void>} Resolves when all images have been loaded and their dimensions set.
*/
async function prepareImagesForPhotoSwipe() {
    // Get all the anchor tags from the image gallery
    const imagesLinksList = document.querySelectorAll(constants.ZOOM_IMAGE_LINK_SELECTOR);
    const promisesList = [];
    imagesLinksList.forEach(element => {
        const promise = new Promise((resolve) => {
            let image = new Image();
            image.src = element.getAttribute("data-pswp-src");
            image.onload = () => {
                element.dataset.pswpWidth = image.width;
                element.dataset.pswpHeight = image.height;
                resolve(); // Resolve the promise only if the image has been loaded
            };

            image.onerror = () => {
                // Set default dimensions for failed images to prevent breaking the gallery
                element.dataset.pswpWidth = 800;
                element.dataset.pswpHeight = 600;
                resolve(); // Resolve instead of reject to continue with other images
            };
        });
        promisesList.push(promise);
    });
    // Wait for all promises to be resolved
    await Promise.all(promisesList);
}

/**
* Initializes the PhotoSwipe lightbox gallery for product images.
* - Selects the container for zoomable images.
* - Preloads image dimensions for accurate display.
* - Configures and creates a PhotoSwipeLightbox instance with custom options.
* - Adds custom UI elements and event listeners to the lightbox.
* - Initializes the PhotoSwipe gallery if the images container exists.
*
* @async
* @returns {Promise<void>} Resolves when PhotoSwipe is initialized or exits early if no images container is found.
*/
async function initPhotoSwipe() {
    const imagesContainer = document.querySelector(constants.ZOOM_IMAGES_WRAPPER_SELECTOR);

    if (!imagesContainer) return;

    // preload images dimensions
    await prepareImagesForPhotoSwipe();

    const lightbox = new PhotoSwipeLightbox(getPhotoSwipeOptions());
    // add custom UI elements
    addUIElements(lightbox);
    // attach event listeners
    handleEventListeners(lightbox);
    // initialize photoSwipe
    lightbox.init();
}

module.exports = {
    initPhotoSwipe
};
